import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { DataGrid } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import toast from "react-hot-toast";
import { Loader } from "../../../Shared/Loader";
import { colors } from "../../../Shared/colors";
import {
  Card, CardHead, Tiles, Tile, Stacked, Muted, Numeric,
  IconAction, GridWrap, Empty, dataGridSx,
} from "../staffStyles";

/**
 * The company phone book.
 *
 * This page is opened to find one person's number, and it had no way to search
 * — you scrolled a paginated grid of everyone. Search is therefore the main
 * control, not a secondary filter, and it matches name, designation, phone and
 * email at once because people search by whatever they happen to remember.
 *
 * Phone and email are now actions rather than text: tapping a number dials it,
 * and both can be copied in one click. On a directory that is the entire job.
 */
export const ContactDirectory = ({
  staff = [],
  isFetching,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
}) => {
  const [search, setSearch] = useState("");

  const rows = useMemo(
    () =>
      (staff || []).map((s) => ({
        id: s._id,
        name: `${s.firstName || ""} ${s.lastName || ""}`.trim(),
        role: s.role,
        designation: s.designation,
        phone: s.phoneNum,
        email: s.email,
        city: s.address,
        isBlock: s.isBlock,
      })),
    [staff]
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    // Digits-only comparison as well, so "0300 123" finds "03001234567"
    // regardless of how the number was typed in either place.
    const digits = needle.replace(/[^\d]/g, "");
    return rows.filter((r) => {
      const haystack = [r.name, r.role, r.designation, r.email, r.city]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const phoneDigits = String(r.phone || "").replace(/[^\d]/g, "");
      return (
        haystack.includes(needle) ||
        (digits.length > 0 && phoneDigits.includes(digits))
      );
    });
  }, [rows, search]);

  const copy = (value, what) => {
    if (!value || !navigator?.clipboard) return;
    navigator.clipboard.writeText(value);
    toast.success(`${what} copied`);
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 190,
        renderCell: (params) => (
          <Stacked>
            <span className="primary">{params.value || "—"}</span>
            {params.row.city ? <span className="sub">{params.row.city}</span> : null}
          </Stacked>
        ),
      },
      {
        field: "role",
        headerName: "Designation",
        width: 138,
        renderCell: (params) => (
          <Muted
            style={{
              textTransform: "uppercase",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
            }}
          >
            {params.value || "—"}
          </Muted>
        ),
      },
      {
        field: "phone",
        headerName: "Phone",
        width: 195,
        renderCell: (params) =>
          params.value ? (
            <PhoneCell>
              <a href={`tel:${params.value}`} onClick={(e) => e.stopPropagation()}>
                <Numeric>{params.value}</Numeric>
              </a>
              <Tooltip title="Copy number">
                <IconAction
                  type="button"
                  aria-label={`Copy ${params.row.name}'s phone number`}
                  onClick={(e) => {
                    e.stopPropagation();
                    copy(params.value, "Phone number");
                  }}
                >
                  <ContentCopyOutlinedIcon />
                </IconAction>
              </Tooltip>
            </PhoneCell>
          ) : (
            <Muted>—</Muted>
          ),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 210,
        renderCell: (params) =>
          params.value ? (
            <PhoneCell>
              <a
                href={`mailto:${params.value}`}
                onClick={(e) => e.stopPropagation()}
                title={params.value}
              >
                {params.value}
              </a>
              <Tooltip title="Copy email">
                <IconAction
                  type="button"
                  aria-label={`Copy ${params.row.name}'s email`}
                  onClick={(e) => {
                    e.stopPropagation();
                    copy(params.value, "Email");
                  }}
                >
                  <ContentCopyOutlinedIcon />
                </IconAction>
              </Tooltip>
            </PhoneCell>
          ) : (
            <Muted>—</Muted>
          ),
      },
    ],
    []
  );

  const reachable = rows.filter((r) => r.phone || r.email).length;

  return (
    <>
      <Tiles>
        <Tile>
          <p className="label">People</p>
          <div className="figure">{rows.length}</div>
          <p className="meta">In the directory</p>
        </Tile>
        <Tile $stripe={colors.textBlue}>
          <p className="label">Contactable</p>
          <div className="figure">{reachable}</div>
          <p className="meta">Have a phone or email on file</p>
        </Tile>
      </Tiles>

      <Card>
        <CardHead>
          <SearchBox>
            <SearchOutlinedIcon />
            <input
              type="search"
              value={search}
              placeholder="Search by name, designation, phone or email"
              aria-label="Search the directory"
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBox>
          <span className="count">
            <strong>{filtered.length}</strong>
            {filtered.length !== rows.length ? ` of ${rows.length}` : ""}{" "}
            {filtered.length === 1 ? "person" : "people"}
          </span>
        </CardHead>

        <GridWrap>
          <DataGrid
            rows={filtered}
            columns={columns}
            loading={isFetching}
            disableSelectionOnClick
            page={page}
            onPageChange={onPageChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            pagination
            rowsPerPageOptions={[25, 50, 100]}
            components={{
              NoRowsOverlay: () => (
                <Empty>
                  <ContactsOutlinedIcon />
                  <h3>{search ? "Nobody matches that search" : "No contacts yet"}</h3>
                  <p>
                    {search
                      ? "Try part of a name, a designation, or the first few digits of a number."
                      : "Staff appear here as they are added to the system."}
                  </p>
                </Empty>
              ),
            }}
            sx={dataGridSx}
          />
          {isFetching ? <Loader /> : null}
        </GridWrap>
      </Card>
    </>
  );
};

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 240px;
  max-width: 420px;
  padding: 0 11px;
  min-height: 40px;
  border-radius: 8px;
  border: 1px solid ${colors.inputBorderWhite};
  background: ${colors.white};

  &:focus-within {
    border-color: ${colors.brown};
  }

  svg {
    font-size: 1.05rem;
    color: ${colors.textLight};
    flex-shrink: 0;
  }
  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.85rem;
    color: ${colors.text};
  }
  input::placeholder {
    color: ${colors.textLight};
  }
`;

const PhoneCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  a {
    color: ${colors.textBlue};
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  a:hover {
    text-decoration: underline;
  }
`;
